/** Matches tree-drawing characters or a bare folder line ending in / or \ */
const TREE_CHAR_PATTERN = /[├└│─]/;
const FOLDER_LINE_PATTERN = /^[A-Za-z0-9_.\/\\-]+[\/\\]\s*$/;

/**
 * Returns true if a line is part of a tree diagram (connector chars or folder header).
 * @param {string} line
 * @returns {boolean}
 */
export function isTreeLine(line) {
    return TREE_CHAR_PATTERN.test(line) || FOLDER_LINE_PATTERN.test(line.trim());
}

/**
 * Splits a tree item line into connector, filename and description (right of ←, if present).
 * @param {string} line
 * @returns {{connector: string, filename: string, desc: string}|null}
 */
export function parseTreeItemLine(line) {
    const arrowIdx = line.indexOf('←');
    const left = arrowIdx === -1 ? line : line.slice(0, arrowIdx);
    const desc = arrowIdx === -1 ? '' : line.slice(arrowIdx + 1).trim();

    const match = left.match(/^([\s│├└─]*)(\S+)\s*$/);
    if (!match) return null;

    return { connector: match[1], filename: match[2], desc };
}

/**
 * Returns true if a block of text looks like a source-tree diagram —
 * at least one folder line and at least two tree-connector lines.
 * @param {string} text - raw multi-line paragraph text
 * @returns {boolean}
 */
export function isTreeBlockText(text) {
    const lines = text.split('\n').filter((l) => l.trim() !== '');
    if (lines.length < 2) return false;

    const connectorLines = lines.filter((l) => TREE_CHAR_PATTERN.test(l)).length;
    return connectorLines >= 2;
}

/**
 * Parses raw multi-line text into a list of row descriptors for rendering.
 * @param {string} text - raw multi-line paragraph text
 * @returns {Array<{kind: 'folder'|'item'|'raw', connector?: string, filename?: string, desc?: string, raw?: string}>}
 */
export function parseTreeBlock(text) {
    const lines = text.split('\n').filter((l) => l.trim() !== '');

    return lines.map((line) => {
        if (TREE_CHAR_PATTERN.test(line)) {
            const item = parseTreeItemLine(line);
            if (item) return { kind: 'item', ...item };
            return { kind: 'raw', raw: line };
        }
        return { kind: 'folder', raw: line.trim() };
    });
}
